import { useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { useSessionStore } from '../store/sessionStore';
import { getAgentInitials } from '../utils/agentColors';
import { Maximize2, AlertTriangle } from 'lucide-react';
import './TopologyGraph.css';

export function TopologyGraph() {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { nodes, edges, anomalies, selectedNodeId, selectNode } = useSessionStore();
  const nodeArray = Object.values(nodes);
  const edgeArray = Object.values(edges).filter((e) => nodes[e.source] && nodes[e.target]);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;
    if (nodeArray.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const { width, height } = containerRef.current.getBoundingClientRect();

    const defs = svg.append('defs');
    defs.append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 22)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', 'var(--border)');

    defs.append('marker')
      .attr('id', 'arrowhead-red')
      .attr('viewBox', '-0 -5 10 10')
      .attr('refX', 22)
      .attr('refY', 0)
      .attr('orient', 'auto')
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .append('path')
      .attr('d', 'M 0,-5 L 10,0 L 0,5')
      .attr('fill', 'var(--accent-red)');

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.3, 4])
      .on('zoom', (e) => {
        g.attr('transform', e.transform.toString());
      });

    svg.call(zoom);

    const g = svg.append('g');

    // Nodes as D3 objects for the simulation
    const simNodes = nodeArray.map((n) => ({
      id: n.id,
      name: n.name,
      color: n.color,
      messageCount: n.messageCount,
      tokenCount: n.tokenCount,
      lastActive: n.lastActive,
      x: n.x ?? width / 2 + (Math.random() - 0.5) * 200,
      y: n.y ?? height / 2 + (Math.random() - 0.5) * 200,
      fx: null as number | null,
      fy: null as number | null,
    }));

    const simLinks = edgeArray.map((e) => ({
      source: e.source,
      target: e.target,
      hitCount: e.hitCount,
      isAnomaly: e.isAnomaly,
    }));

    const simulation = d3.forceSimulation(simNodes as d3.SimulationNodeDatum[])
      .force('link', d3.forceLink(simLinks as d3.SimulationLinkDatum<d3.SimulationNodeDatum>[])
        .id((d) => (d as { id: string }).id)
        .distance(120)
        .strength(0.5) as d3.Force<d3.SimulationNodeDatum, undefined>)
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collide', d3.forceCollide().radius(40));

    const edgeGroup = g.append('g').attr('class', 'edges');
    const nodeGroup = g.append('g').attr('class', 'nodes');

    function updateGraph() {
      // Edges
      const lines = edgeGroup
        .selectAll<SVGLineElement, typeof simLinks[0]>('line')
        .data(simLinks, (d) => `${(d as { source: string }).source}→${(d as { target: string }).target}`);

      lines.exit().remove();

      lines.enter()
        .append('line')
        .attr('stroke', (d) => (d as { isAnomaly: boolean }).isAnomaly ? 'var(--accent-red)' : 'var(--border)')
        .attr('stroke-width', (d) => (d as { isAnomaly: boolean }).isAnomaly ? 2 : 1.5)
        .attr('marker-end', (d) => (d as { isAnomaly: boolean }).isAnomaly ? 'url(#arrowhead-red)' : 'url(#arrowhead)');

      // Nodes
      const groups = nodeGroup
        .selectAll<SVGGElement, typeof simNodes[0]>('.node-group')
        .data(simNodes as d3.SimulationNodeDatum[], (d) => (d as { id: string }).id);

      groups.exit().remove();

      const enter = groups.enter()
        .append('g')
        .attr('class', 'node-group')
        .attr('style', 'animation: scale-in 200ms ease-out')
        .call(
          d3.drag<SVGGElement, typeof simNodes[0]>()
            .on('start', (event) => {
              if (!event.active) simulation.alphaTarget(0.3).restart();
              (event.subject as { fx: number | null; fy: number | null }).fx = (event.subject as d3.SimulationNodeDatum).x ?? null;
              (event.subject as { fx: number | null; fy: number | null }).fy = (event.subject as d3.SimulationNodeDatum).y ?? null;
            })
            .on('drag', (event) => {
              (event.subject as d3.SimulationNodeDatum).x = event.x;
              (event.subject as d3.SimulationNodeDatum).y = event.y;
            })
            .on('end', (event) => {
              if (!event.active) simulation.alphaTarget(0);
              (event.subject as { fx: number | null; fy: number | null }).fx = null;
              (event.subject as { fx: number | null; fy: number | null }).fy = null;
            }) as any
        );

      enter.append('circle').attr('r', (d) => {
        const mc = (d as { messageCount: number }).messageCount ?? 0;
        return Math.max(20, Math.min(40, 20 + Math.log2(mc + 1) * 6));
      })
        .attr('fill', (d) => (d as { color: string }).color)
        .attr('opacity', 0.9)
        .attr('stroke', 'var(--bg-deep)')
        .attr('stroke-width', 2);

      enter.append('text')
        .attr('class', 'node-label')
        .attr('text-anchor', 'middle')
        .attr('dy', '0.35em')
        .attr('fill', 'var(--bg-deep)')
        .attr('font-family', 'var(--font-mono)')
        .attr('font-size', '10px')
        .attr('font-weight', '600')
        .text((d) => getAgentInitials((d as { id: string }).id));

      enter.on('click', (_event, d) => {
        selectNode((d as { id: string }).id);
      });

      enter.append('title').text((d) => {
        const nd = d as { name: string; messageCount: number; tokenCount: number; lastActive: number };
        return `${nd.name}\n${nd.messageCount} messages\n${nd.tokenCount} tokens`;
      });

      // Anomaly ring
      enter.filter((d) => {
        const id = (d as { id: string }).id;
        return anomalies.some((a) => a.involved_agents.includes(id));
      })
        .append('circle')
        .attr('r', (d) => {
          const mc = (d as { messageCount: number }).messageCount ?? 0;
          return Math.max(20, Math.min(40, 20 + Math.log2(mc + 1) * 6)) + 4;
        })
        .attr('fill', 'none')
        .attr('stroke', 'var(--accent-red)')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '4 2')
        .attr('class', 'edge-anomaly pulse-error');

      // Update positions on tick
      simulation.on('tick', () => {
        edgeGroup.selectAll('line')
          .attr('x1', (d) => ((d as { source: d3.SimulationNodeDatum }).source as d3.SimulationNodeDatum).x ?? 0)
          .attr('y1', (d) => ((d as { source: d3.SimulationNodeDatum }).source as d3.SimulationNodeDatum).y ?? 0)
          .attr('x2', (d) => ((d as { target: d3.SimulationNodeDatum }).target as d3.SimulationNodeDatum).x ?? 0)
          .attr('y2', (d) => ((d as { target: d3.SimulationNodeDatum }).target as d3.SimulationNodeDatum).y ?? 0);

        nodeGroup.selectAll<SVGGElement, typeof simNodes[0]>('.node-group')
          .attr('transform', (d) => `translate(${(d as d3.SimulationNodeDatum).x ?? 0},${(d as d3.SimulationNodeDatum).y ?? 0})`)
          .select('circle:first-of-type')
          .attr('stroke', (d) => (d as { id: string }).id === selectedNodeId ? 'var(--accent-blue)' : 'var(--bg-deep)')
          .attr('stroke-width', (d) => (d as { id: string }).id === selectedNodeId ? 3 : 2);
      });
    }

    updateGraph();

    return () => {
      simulation.stop();
    };
  }, [nodeArray, edgeArray, anomalies, selectedNodeId, selectNode]);

  const fitToView = useCallback(() => {
    if (!svgRef.current || !containerRef.current) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    const svg = d3.select(svgRef.current);
    svg.transition().duration(500).call(
      d3.zoom<SVGSVGElement, unknown>().transform as unknown as (
        t: d3.Transition<SVGSVGElement, unknown, null, undefined>,
        ...args: unknown[]
      ) => void,
      d3.zoomIdentity.translate(width / 2, height / 2).scale(1.1)
    );
  }, []);

  const hasAnomalies = anomalies.length > 0;
  const isEmpty = Object.keys(nodes).length === 0;

  return (
    <div className="topology-graph" ref={containerRef}>
      {isEmpty ? (
        <div className="graph-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="12" cy="12" r="3" />
            <circle cx="4" cy="4" r="2" opacity="0.4" />
            <circle cx="20" cy="4" r="2" opacity="0.4" />
            <circle cx="20" cy="20" r="2" opacity="0.4" />
            <circle cx="4" cy="20" r="2" opacity="0.4" />
            <line x1="12" y1="9" x2="6" y2="5" opacity="0.4" />
            <line x1="15" y1="12" x2="18" y2="5" opacity="0.4" />
            <line x1="15" y1="12" x2="18" y2="19" opacity="0.4" />
            <line x1="12" y1="15" x2="6" y2="19" opacity="0.4" />
          </svg>
          <p className="graph-empty-title">Waiting for agent events…</p>
          <p className="graph-empty-subtitle">Connect the SDK and run a workflow</p>
        </div>
      ) : (
        <>
          <svg ref={svgRef} className="graph-svg" />
          <div className="graph-controls">
            <button className="graph-btn" onClick={fitToView} title="Fit to view">
              <Maximize2 size={14} />
            </button>
            {hasAnomalies && (
              <button className="graph-btn graph-btn--alert" title="Focus anomaly">
                <AlertTriangle size={14} />
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
